import type {
  AudiencePreview,
  AudienceSpec,
  CommsTemplate,
  ComposeMessageInput,
  EventCommsOverview,
  EventCommsSendInput,
  EventInviteInput,
  EventInviteResult,
  InboxPage,
  MemberCommsItem,
  MessageCategory,
  MessageDetail,
  MessageSummary,
  NudgeRequestInput,
  NudgeResult,
  TemplateInput,
  UpdateTemplateInput,
} from '@gwc/shared';
import { api } from '../lib/api';

export const inboxApi = {
  list: (params: { limit?: number; cursor?: string; unreadOnly?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (params.limit) q.set('limit', String(params.limit));
    if (params.cursor) q.set('cursor', params.cursor);
    if (params.unreadOnly) q.set('unreadOnly', 'true');
    const qs = q.toString();
    return api.get<{ data: InboxPage }>(`/inbox${qs ? `?${qs}` : ''}`).then((r) => r.data);
  },
  unreadCount: () =>
    api
      .get<{ data: { unreadCount: number } }>('/inbox/unread-count')
      .then((r) => r.data.unreadCount),
  markRead: (id: string) => api.post(`/inbox/${id}/read`, {}),
  markAllRead: () => api.post<{ data: { marked: number } }>('/inbox/read-all', {}),
  archive: (id: string) => api.post(`/inbox/${id}/archive`, {}),
};

export interface SendResult {
  id: string;
  status: string;
  recipientCount: number;
}

export interface CommsEventOption {
  id: string;
  title: string;
  eventYear: number;
  eventMonth: number | null;
  status: string;
}

export const commsApi = {
  // Audience + compose
  /** With `eventId`, a test event's audience is clamped to its roster, as the send will be. */
  previewAudience: (audience: AudienceSpec, category: MessageCategory, eventId?: string) =>
    api
      .post<{ data: AudiencePreview }>('/comms/audience/preview', { audience, category, eventId })
      .then((r) => r.data),
  /** Resolve merge fields against a tasting, exactly as the send will. */
  previewText: (title: string, body: string, eventId?: string) =>
    api
      .post<{ data: { title: string; body: string; link: string | null } }>('/comms/preview', {
        title,
        body,
        eventId,
      })
      .then((r) => r.data),
  send: (input: ComposeMessageInput) =>
    api.post<{ data: SendResult }>('/comms/messages', input).then((r) => r.data),
  sendTest: (input: ComposeMessageInput) =>
    api
      .post<{ data: { id: string; sent: { push: number; email: number } } }>(
        '/comms/messages/test',
        input,
      )
      .then((r) => r.data),

  // History + tracking
  history: (params: { eventId?: string; origin?: 'leadership' | 'all'; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.eventId) q.set('eventId', params.eventId);
    if (params.origin) q.set('origin', params.origin);
    if (params.limit) q.set('limit', String(params.limit));
    const qs = q.toString();
    return api
      .get<{ data: MessageSummary[] }>(`/comms/messages${qs ? `?${qs}` : ''}`)
      .then((r) => r.data);
  },
  message: (id: string) =>
    api.get<{ data: MessageDetail }>(`/comms/messages/${id}`).then((r) => r.data),
  retry: (id: string) =>
    api.post<{ data: { retried: number } }>(`/comms/messages/${id}/retry`, {}).then((r) => r.data),
  cancel: (id: string) => api.post(`/comms/messages/${id}/cancel`, {}),
  events: () => api.get<{ data: CommsEventOption[] }>('/comms/events').then((r) => r.data),

  // Templates
  templates: () => api.get<{ data: CommsTemplate[] }>('/comms/templates').then((r) => r.data),
  createTemplate: (input: TemplateInput) =>
    api.post<{ data: CommsTemplate }>('/comms/templates', input).then((r) => r.data),
  updateTemplate: (id: string, patch: UpdateTemplateInput) =>
    api.patch<{ data: CommsTemplate }>(`/comms/templates/${id}`, patch).then((r) => r.data),
  deleteTemplate: (id: string) => api.del(`/comms/templates/${id}`),
  resetTemplate: (id: string) =>
    api.post<{ data: CommsTemplate }>(`/comms/templates/${id}/reset`, {}).then((r) => r.data),
};

/** A tasting's own communications (announcement, reminders, attendee messages). */
export const eventCommsApi = {
  overview: (eventId: string) =>
    api.get<{ data: EventCommsOverview }>(`/events/${eventId}/comms`).then((r) => r.data),
  announce: (eventId: string) =>
    api
      .post<{ data: { id: string | null; alreadySent: boolean } }>(
        `/events/${eventId}/comms/announce`,
        {},
      )
      .then((r) => r.data),
  send: (eventId: string, input: EventCommsSendInput) =>
    api.post<{ data: SendResult }>(`/events/${eventId}/comms/send`, input).then((r) => r.data),
  /** Open the tasting to more member types (and, by default, announce it to them). */
  invite: (eventId: string, input: Partial<EventInviteInput> & Pick<EventInviteInput, 'memberTypes'>) =>
    api
      .post<{ data: EventInviteResult }>(`/events/${eventId}/comms/invite`, input)
      .then((r) => r.data),
  /** Remind the people in one state (or just `personIds` of them). */
  nudge: (eventId: string, input: Partial<NudgeRequestInput> & Pick<NudgeRequestInput, 'reason'>) =>
    api.post<{ data: NudgeResult }>(`/events/${eventId}/nudges`, input).then((r) => r.data),
};

export const memberCommsApi = {
  history: (personId: string) =>
    api.get<{ data: MemberCommsItem[] }>(`/members/${personId}/comms`).then((r) => r.data),
};
